import axios, { AxiosResponse } from 'axios';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Reject } from '../Reject';
import { logout } from './auth';
import { handleApiError } from '../handleApiError';
import { GeocodeFeature } from '@mapbox/mapbox-sdk/services/geocoding';

interface LocationState {
    searchKey: string;
    searchingLocationByName: boolean;
    locationByNameError: boolean;
    locationByNameSuccess: boolean;
    searchError: string;
    places: GeocodeFeature[];
}

const initialState: LocationState = {
    searchKey: '',
    searchingLocationByName: false,
    locationByNameError: false,
    locationByNameSuccess: false,
    searchError: '',
    places: [],
};

interface SearchLocationByNameParams {
    location: string;
}

interface SearchLocationByNameResponse {
    places: GeocodeFeature[];
}

export const searchLocationByName = createAsyncThunk<SearchLocationByNameResponse, SearchLocationByNameParams, Reject>(
    'searchLocationByName',
    (params, { rejectWithValue }) => {
        if (params.location.length < 2) {
            return rejectWithValue({ message: 'String not long enough to search.' });
        }
        return axios
            .get(
                `https://api.mapbox.com/geocoding/v5/mapbox.places/${params.location}.json?access_token=${process.env.REACT_APP_MAPBOX_PUBLIC_TOKEN}`
            )
            .then(
                (
                    response: AxiosResponse<{
                        features: GeocodeFeature[];
                    }>
                ) => {
                    if (response && response.data) {
                        return {
                            places: response.data.features,
                        };
                    }
                }
            )
            .catch((error) => {
                const apiError = handleApiError(error);
                return rejectWithValue(apiError);
            });
    }
);

const locationSlice = createSlice({
    name: 'location',
    initialState,
    reducers: {
        clearSearchByName: (state) => {
            state.searchingLocationByName = false;
            state.locationByNameError = false;
            state.locationByNameSuccess = false;
            state.searchKey = '';
            state.places = [];
        },
        setLocation: (state, action: PayloadAction<string>) => {
            state.searchKey = action.payload ?? ''
        },
        generateSampleSearch: (state) => {
            state.searchingLocationByName = false;
            state.locationByNameError = false;
            state.locationByNameSuccess = true;
            state.searchKey = 'Hyde Park';
            state.places = [{
                'id': 'neighborhood.310258924',
                'type': 'Feature',
                'place_type': ['neighborhood'],
                'relevance': 1,
                'text': 'Hyde Park',
                'place_name': 'Hyde Park, Chicago, Illinois, United States',
                'bbox': [-87.606415, 41.78627, -87.575167, 41.80349],
                'center': [-87.587364, 41.799576],
            }, {
                'id': 'place.157681900',
                'type': 'Feature',
                'place_type': ['place'],
                'relevance': 1,
                'text': 'Hyde Park',
                'place_name': 'Hyde Park, Massachusetts, United States',
                'bbox': [-71.150836, 42.225884, -71.097669, 42.278039],
                'center': [-71.12158, 42.256016],
            }, {
                'id': 'neighborhood.310070508',
                'type': 'Feature',
                'place_type': ['neighborhood'],
                'relevance': 1,
                'text': 'Hyde Park',
                'place_name': 'Hyde Park, Los Angeles, California, United States',
                'bbox': [-118.358616, 33.967249, -118.317116, 34.003704],
                'center': [-118.330864, 33.98475],
            }, {
                'id': 'place.157673708',
                'type': 'Feature',
                'place_type': ['place'],
                'relevance': 1,
                'text': 'Hyde Park',
                'place_name': 'Hyde Park, New York, United States',
                'bbox': [-73.957743, 41.739537, -73.824176, 41.843903],
                'center': [-73.9363, 41.79148],
            }, {
                'id': 'neighborhood.23489615',
                'type': 'Feature',
                'place_type': ['neighborhood'],
                'relevance': 1,
                'text': 'Hyde Park',
                'place_name': 'Hyde Park, Leeds, West Yorkshire, England, United Kingdom',
                'bbox': [-1.574594, 53.808453, -1.561119, 53.816409],
                'center': [-1.5691, 53.81232],
            }] as GeocodeFeature[]
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(searchLocationByName.pending, (state) => {
                state.searchingLocationByName = true;
                state.locationByNameError = false;
                state.locationByNameSuccess = false;
                state.searchError = '';
                state.places = [];
            })
            .addCase(searchLocationByName.rejected, (state, action) => {
                state.searchingLocationByName = false;
                state.locationByNameError = true;
                state.locationByNameSuccess = false;
                state.searchError = action.payload.message;
                state.places = [];
            })
            .addCase(searchLocationByName.fulfilled, (_, action) => {
                const newState = { ...initialState };
                newState.locationByNameSuccess = true;
                newState.places = action.payload.places;
                return newState;
            })
            .addCase(logout.toString(), () => ({ ...initialState }));
    },
});

export const { clearSearchByName, setLocation, generateSampleSearch } = locationSlice.actions;
export default locationSlice.reducer;
